/**
 * @file useDesktopPin.ts
 * @description Answers, for one pin path, whether the signed-in user keeps it
 * and at what scope. Consumers: the pin control on the form and record
 * toolbars, and the per-file toggle in the attachments panel.
 *
 * `pinPath` must already be folded by `resolveDesktopPinPath`; pass `null` when
 * the caller has no pinnable path (a bare entity, or an id not yet known) and
 * the hook reads as unpinned without special-casing at the call site.
 *
 * When the map holds duplicate entries for the path, the oldest speaks for it —
 * the same entry `keep` reuses and `stopKeeping` removes first, so the control
 * never shows a scope that the next write would discard.
 *
 * The returned object and its `scope` are memoized on the pins snapshot, which
 * only moves when the map itself changes. `resolveDesktopPinScope` allocates a
 * fresh object on every call, so without this a caller putting `scope` in a
 * dependency array would loop forever.
 */

import { useMemo } from 'react';

import type { DesktopPinScope } from '#types';
import { resolveDesktopPinEntries } from './resolveDesktopPinEntries';
import { resolveDesktopPinScope } from './resolveDesktopPinScope';
import { useDesktopPins } from './useDesktopPins';

export type DesktopPinState = {
    isPinned: boolean;
    /** `null` when not pinned. Never inferred from an absent scope — that reads as whole-document. */
    scope: DesktopPinScope | null;
    loading: boolean;
};

export const useDesktopPin = (pinPath: string | null | undefined): DesktopPinState => {
    const { pins, loading } = useDesktopPins();

    return useMemo<DesktopPinState>(() => {
        const entry = pinPath ? resolveDesktopPinEntries(pins, pinPath)[0] : undefined;
        return {
            isPinned: entry !== undefined,
            scope: entry ? resolveDesktopPinScope(entry.pin) : null,
            loading,
        };
    }, [pins, pinPath, loading]);
};
